import type { LoginState } from "./oidcConfig";

import { resetInteractiveRedirectGuard, startInteractiveRedirectOnce } from "./redirectGuard";
import { getOidcUserManager } from "./userManagerSingleton";

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function getCurrentPathWithQuery(): string {
  if (!isBrowser()) return "/";
  const path = `${window.location.pathname}${window.location.search}`;
  // Never use logout/callback as returnTo after login.
  if (path === "/logout" || path.startsWith("/callback")) return "/";
  return path;
}

/**
 * Triggers an interactive OIDC signin redirect (single-flight guarded).
 *
 * This is intentionally non-React so it can be shared by:
 * - `lib/auth/AuthProvider.tsx`
 * - `lib/auth/handle401Error.ts` (401 redirects)
 */
export async function redirectToInteractiveLogin(returnTo?: string): Promise<void> {
  if (!isBrowser()) return;

  // Never attempt interactive login from within the callback route.
  if (window.location.pathname === "/callback") return;

  if (!startInteractiveRedirectOnce()) return;

  try {
    const userManager = getOidcUserManager();
    const state: LoginState = { returnTo: returnTo ?? getCurrentPathWithQuery() };
    await userManager.signinRedirect({ state });
  } catch (error) {
    // If redirect fails synchronously, allow retry attempts.
    resetInteractiveRedirectGuard();
    throw error;
  }
}

